import React from 'react';
import { useHistory } from 'react-router-dom';
import { LayoutPage, LayoutContent, Errors, SaveAndCompleteLaterButton } from 'components';
import { Container, Text, List, Stack, Grid, Button } from 'basis';
import { useStoreValue } from 'store';
import { isMobileOnly } from 'react-device-detect';
import { useInitBiometrics, useErrorTrackingAndUpdateStore } from 'hooks';
import { DEV_FEATURES } from '__dev/devFeatures';
import { STEP_PROCESSING_BIOMETRICS } from '_config';
import styled from '@emotion/styled';

export const ProofOfIdentityNz = () => {
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Verify now');
  const [store, updateStore] = useStoreValue();
  const history = useHistory();
  /** Note `isMobileOnly` is excluding tablet devices as APLYiD currently has issues with treating tablets as desktops. After they fix it we can change to use `isMobile` instead */
  const isMobileOrSimulated = isMobileOnly || DEV_FEATURES.SIMULATE_MOBILE;

  const onInitBiometricsSuccess = ({ startUrl }) => {
    updateStore({
      applicationErrors: null,
      activeStep: STEP_PROCESSING_BIOMETRICS,
    });

    if (isMobileOrSimulated) {
      window.location.assign(startUrl);
    } else {
      history.push(`${STEP_PROCESSING_BIOMETRICS}?return=true`);
    }
  };

  const { initBiometrics, loading: startingBiometrics } = useInitBiometrics({
    applicationId: store.applicationId,
    mobileNumber: store.acquisition.contactDetails.mobileNumber,
    onError: handleErrorCallback,
    onSuccess: onInitBiometricsSuccess,
  });

  const onBiometricsVerify = () => {
    updateStore({
      biometricsVerification: 'STARTED',
    });

    initBiometrics({
      useDesktopFlow: !isMobileOrSimulated,
    });
  };

  if (store.applicationErrors) {
    return (
      <LayoutPage>
        <Errors applicationRef={store.applicationRef} />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      <Container margin="0 0 13 0" hasBreakpointWidth>
        <LayoutContent rowsGap="0">
          <Stack gap="8">
            <Container>
              <Text>
                To protect New Zealanders from identity fraud, we use facial verification technology to bio metrically
                match your face to the picture on your ID. THe process is{' '}
                <strong>quick, simple and secure, it should only take 3 minutes to complete</strong>. If required, we
                may use this information for additional identity verification.
              </Text>
            </Container>
            <Text textStyle="heading5">Digitally verify your identity (using APLYiD)</Text>
            <StyledList>
              <List>
                <List.Item>
                  <strong>Take a picture</strong> of your New Zealand Passport or Driver Licence
                </List.Item>
                <List.Item>
                  <strong>Verify that the details</strong> captured off your Driver Licence or Passport are correct
                </List.Item>
                <List.Item>
                  <strong>Take a live selfie of your face,</strong> on-screen prompts will guide you through the process
                </List.Item>
              </List>
            </StyledList>
            <Buttons onClick={onBiometricsVerify} loading={startingBiometrics} buttonLabel="Verify now" />
          </Stack>
        </LayoutContent>
      </Container>
    </LayoutPage>
  );
};

const StyledList = styled.div`
  li::before {
    background-color: black;
    width: 0.3em;
    height: 0.3em;
  }
  li:not(:first-of-type) {
    margin-top: 2px;
  }
`;

const FormButtonWrapper = styled.div`
  & button {
    padding: 12px 16px;
  }
`;

function Buttons({ onClick, loading, buttonLabel }) {
  return (
    <Grid rowsGap="4" colsGap="10" cols="2" margin="4 0 0 0">
      <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
        <FormButtonWrapper>
          <Grid>
            <Button onClick={onClick} loading={loading} padding="0 2">
              {buttonLabel}
            </Button>
          </Grid>
        </FormButtonWrapper>
      </Grid.Item>
      <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="0">
        <FormButtonWrapper>
          <Grid>
            <SaveAndCompleteLaterButton />
          </Grid>
        </FormButtonWrapper>
      </Grid.Item>
    </Grid>
  );
}
